<template>
    <div :class="['autocomplete input-group flex-nowrap', props.wrapclass]">
        
        <!-- Prefix Text -->
        <div 
            v-if="props.prefix || slots.prefix" 
            class="input-group-text"
            :class="props.prefixClass"
        >
            <slot name="prefix"></slot>
            <span v-html="props.prefix"></span>
        </div>
        
        <!-- Input with KtDropmenu integration -->
        <div class="ac-input-wrap flex-grow-1 position-relative">
            <!-- Selected items display for multiple selection -->
            <div v-if="props.multiple && selectedOptions.length" class="selected-items mb-2">
                <span 
                    v-for="option in selectedOptions" 
                    :key="getOptionKey(option, 0)"
                    class="badge bg-primary me-1 mb-1"
                >
                    {{ getOptionDisplay(option) }}
                    <button 
                        type="button" 
                        class="btn-close btn-close-white ms-1" 
                        @click="selectOption(option)"
                        style="font-size: 0.7em;"
                    ></button>
                </span>
            </div>
            
            <input
                ref="inputRef"
                type="text"
                :class="['form-control', props.inputclass]"
                :placeholder="props.placeholder"
                v-model="inputValue"
                @input="handleInput"
                @change="handleChange"
                @focus="handleFocus"
                @keydown="handleKeydown"
                @compositionend="handleCompositionEnd"
                :autocomplete="props.autocomplete || 'off'"
                :disabled="props.disabled"
                :readonly="props.readonly"
            />
            
            <!-- Loading indicator or Clear button -->
            <div class="input-actions">
                <div v-if="isLoading" class="loading-spinner">
                    <i class="fas fa-spinner fa-spin"></i>
                </div>
                <button 
                    v-else-if="inputValue && !props.readonly"
                    type="button"
                    class="btn-clear"
                    @click.stop="clearInput"
                    @mousedown.prevent
                    tabindex="-1"
                >
                    <i class="fas fa-times"></i>
                </button>
            </div>

            <!-- Custom dropdown for autocomplete -->
            <div 
                v-if="dropdownVisible && props.options.length > 0"
                ref="dropdownRef"
                class="autocomplete-dropdown dropdown-menu show"
                :style="dropdownMenuStyle"
                @mousedown.prevent
            >
                <slot name="dropdown" :filtered="filteredOptions" :selectOption="selectOption">
                    <div class="autocomplete-options" :style="dropdownStyle">
                        <div 
                            v-for="(option, index) in filteredOptions" 
                            :key="getOptionKey(option, index)"
                        >
                            <div
                              @click="selectOption(option)"
                              @mouseenter="selectedIndex = index"
                            >
                                <slot name="option" :option="option" :index="index" :selected="isOptionSelected(option)">
                                    <div
                                        class="dropdown-item autocomplete-option"
                                        :class="{ 
                                            'active': index === selectedIndex,
                                            'selected': isOptionSelected(option)
                                        }"
                                    >
                                        <div class="d-flex align-items-center">
                                            <i v-if="props.multiple && isOptionSelected(option)" class="fas fa-check me-2 text-primary"></i>
                                            <span v-if="props.html" v-html="getOptionDisplay(option)"></span>
                                            <span v-else>{{ getOptionDisplay(option) }}</span>
                                        </div>
                                    </div>
                                </slot>
                            </div>
                        </div>
                        <div v-if="isLoading" class="dropdown-item text-muted text-center">
                            <i class="fas fa-spinner fa-spin me-2"></i>
                            Loading...
                        </div>
                        
                        <div v-else-if="!filteredOptions.length && inputValue" class="dropdown-item text-muted">
                            <slot name="no-data">
                                {{ props.noDataText }}
                            </slot>
                        </div>
                    </div>
                </slot>
            </div>
        </div>

        <!-- Suffix Text -->
        <div 
            v-if="props.suffix || slots.suffix" 
            class="input-group-text"
            :class="props.suffixClass"
        >
            <slot name="suffix"></slot>
            <span v-html="props.suffix"></span>
        </div>

    </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted, useSlots, nextTick, type PropType, getCurrentInstance } from "vue";

const slots = useSlots();

interface AutocompleteOption {
    [key: string]: any;
}

const props = defineProps({
    wrapclass: {
        type: String,
        default: ""
    },
    inputclass: {
        type: String,
        default: ""
    },
    prefixClass: {
        type: String,
        default: ""
    },
    suffixClass: {
        type: String,
        default: ""
    },
    options: {
        type: Array as () => AutocompleteOption[],
        default: () => []
    },
    placeholder: {
        type: String,
        default: ""
    },
    // New modern props
    displayBy: {
        type: [String, Function] as PropType<string | ((item: any) => string)>,
        default: ""
    },
    valueBy: {
        type: [String, Function] as PropType<string | ((item: any) => any)>,
        default: ""
    },
    multiple: {
        type: Boolean,
        default: false
    },
    returnObject: {
        type: Boolean,
        default: false
    },
    // Legacy props for backward compatibility
    rowkey: {
        type: String,
        default: ""
    },
    rowvalue: {
        type: String,
        default: ""
    },
    html: {
        type: Boolean,
        default: false
    },
    size: {
        type: [String, Number],
        default: ""
    },
    prefix: {
        type: String,
        default: ""
    },
    suffix: {
        type: String,
        default: ""
    },
    autocomplete: {
        type: String,
        default: "off"
    },
    readonly: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    minLength: {
        type: Number,
        default: 0
    },
    debounceMs: {
        type: Number,
        default: 300
    },
    serverSide: {
        type: Boolean,
        default: false
    },
    noDataText: {
        type: String,
        default: "No data available"
    },
    searchMode: {
        type: String as PropType<'contains' | 'startsWith'>,
        default: 'contains'
    },
    minWidth: {
        type: [String, Number],
        default: ""
    }
});

const emit = defineEmits<{
    change: [value: any];
    update: [filter: { search: string; page?: number; size?: number }];
}>();

const model = defineModel<any>({
    default: ""
});

// Reactive state
const inputRef = ref<HTMLInputElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const inputValue = ref<string>("");
const dropdownVisible = ref<boolean>(false);
const selectedIndex = ref<number>(-1);
const selectedOptions = ref<AutocompleteOption[]>([]);
const isLoading = ref<boolean>(false);
const searchTimeout = ref<NodeJS.Timeout | null>(null);
const debouncedSearch = ref<string>("");
const clickOutsideEnabled = ref<boolean>(true);

// Check if parent is listening to update event (similar to KtDatatable)
const hasUpdateListener = computed(() => {
    const instance = getCurrentInstance();
    return !!(instance?.vnode.props?.onUpdate);
});

// Computed for backward compatibility and modern prop support
const effectiveDisplayBy = computed(() => {
    if (props.displayBy) return props.displayBy;
    if (props.rowvalue) return props.rowvalue;
    return 'name';
});

const effectiveValueBy = computed(() => {
    if (props.valueBy) return props.valueBy;
    if (props.rowkey) return props.rowkey;
    return 'id';
});

// Computed properties
const filteredOptions = computed(() => {
    if (!props.options || !Array.isArray(props.options)) {
        return [];
    }
    
    // If we have an update listener or serverSide is true, assume data is already filtered by server
    if (hasUpdateListener.value || props.serverSide) {
        return props.options;
    }
    
    // Client-side filtering
    if (!debouncedSearch.value || debouncedSearch.value.length < props.minLength) {
        return props.options;
    }
    
    const searchTerm = debouncedSearch.value.toLowerCase().trim();
    if (!searchTerm) {
        return props.options;
    }
    
    return props.options.filter(option => {
        const displayValue = getOptionDisplay(option).toLowerCase();
        return props.searchMode === 'startsWith'
            ? displayValue.startsWith(searchTerm)
            : displayValue.includes(searchTerm);
    });
});

const dropdownMenuStyle = computed(() => {
    const style: any = {};
    
    if (props.minWidth) {
        style.minWidth = typeof props.minWidth === 'number' 
            ? `${props.minWidth}px` 
            : props.minWidth;
    }
    
    return style;
});

const dropdownStyle = computed(() => {
    const style: any = {};
    
    if (props.size) {
        const maxHeight = typeof props.size === 'number' 
            ? `${props.size * 35}px` 
            : props.size;
        style.maxHeight = maxHeight;
        style.overflowY = 'auto';
    }
    
    return style;
});

// Helper functions
const getOptionDisplay = (option: AutocompleteOption): string => {
    if (!option) return "";
    
    const displayBy = effectiveDisplayBy.value;
    
    if (typeof displayBy === 'function') {
        return String(displayBy(option));
    }
    
    if (typeof displayBy === 'string' && option[displayBy] !== undefined) {
        return String(option[displayBy]);
    }
    
    // Fallback logic
    if (option.name !== undefined) {
        return String(option.name);
    }
    
    if (option.id !== undefined) {
        return String(option.id);
    }
    
    return String(option);
};

const getOptionKey = (option: AutocompleteOption, index: number): string => {
    const valueBy = effectiveValueBy.value;
    
    if (typeof valueBy === 'function') {
        return String(valueBy(option));
    }
    
    if (typeof valueBy === 'string' && option[valueBy] !== undefined) {
        return String(option[valueBy]);
    }
    
    return `option-${index}`;
};

const getOptionValue = (option: AutocompleteOption): any => {
    if (!option) return "";
    
    if (props.returnObject) {
        return option;
    }
    
    const valueBy = effectiveValueBy.value;
    
    if (typeof valueBy === 'function') {
        return valueBy(option);
    }
    
    if (typeof valueBy === 'string' && option[valueBy] !== undefined) {
        return option[valueBy];
    }
    
    return option;
};

const isOptionSelected = (option: AutocompleteOption): boolean => {
    if (props.multiple) {
        const modelArray = Array.isArray(model.value) ? model.value : [];
        const optionValue = getOptionValue(option);
        return modelArray.some(val => {
            if (props.returnObject) {
                return JSON.stringify(val) === JSON.stringify(optionValue);
            }
            return val === optionValue;
        });
    }
    
    const optionValue = getOptionValue(option);
    if (props.returnObject) {
        return JSON.stringify(model.value) === JSON.stringify(optionValue);
    }
    return model.value === optionValue;
};

// Event handlers
const handleInput = (event: Event) => {
    if ((event as InputEvent).isComposing) return;
    if (inputValue.value.length >= props.minLength) {
        // When user types, click-outside should be enabled (no mouse event conflict)
        clickOutsideEnabled.value = true;
        dropdownVisible.value = true;
        selectedIndex.value = -1;
        
        // Handle server-side filtering with debouncing
        if (hasUpdateListener.value || props.serverSide) {
            handleServerSideSearch();
        } else {
            // Client-side filtering with debouncing
            handleClientSideSearch();
        }
    } else {
        dropdownVisible.value = false;
        clearSearchTimeout();
    }
};

const handleChange = () => {
    if (inputValue.value.length >= props.minLength) {
        if (!dropdownVisible.value) {
            dropdownVisible.value = true;
            selectedIndex.value = -1;
        }
        
        if (hasUpdateListener.value || props.serverSide) {
            handleServerSideSearch();
        } else {
            handleClientSideSearch();
        }
    }
};

const handleCompositionEnd = () => {
    if (inputValue.value.length >= props.minLength) {
        dropdownVisible.value = true;
        selectedIndex.value = -1;
        if (hasUpdateListener.value || props.serverSide) {
            handleServerSideSearch();
        } else {
            handleClientSideSearch();
        }
    } else {
        dropdownVisible.value = false;
    }
};

const handleServerSideSearch = () => {
    clearSearchTimeout();
    
    searchTimeout.value = setTimeout(() => {
        if (inputValue.value.length >= props.minLength) {
            isLoading.value = true;
            emit('update', {
                search: inputValue.value,
                page: 0,
                size: 50 // Default page size, can be made configurable
            });
        }
    }, props.debounceMs);
};

const handleClientSideSearch = () => {
    clearSearchTimeout();
    
    searchTimeout.value = setTimeout(() => {
        debouncedSearch.value = inputValue.value;
    }, props.debounceMs);
};

const clearSearchTimeout = () => {
    if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
        searchTimeout.value = null;
    }
};

const handleFocus = () => {
    if (inputValue.value.length >= props.minLength || props.minLength === 0) {
        // Temporarily disable click-outside detection to prevent immediate close
        // when clicking input field to open dropdown
        clickOutsideEnabled.value = false;
        dropdownVisible.value = true;
        
        // Re-enable click-outside after a short delay to allow click event to complete
        setTimeout(() => {
            clickOutsideEnabled.value = true;
        }, 100);
    }
};

const handleKeydown = (event: KeyboardEvent) => {
    if (!dropdownVisible.value) return;
    
    switch (event.key) {
        case 'ArrowDown':
            event.preventDefault();
            selectedIndex.value = Math.min(selectedIndex.value + 1, filteredOptions.value.length - 1);
            break;
            
        case 'ArrowUp':
            event.preventDefault();
            selectedIndex.value = Math.max(selectedIndex.value - 1, -1);
            break;
            
        case 'Enter':
            event.preventDefault();
            if (selectedIndex.value >= 0 && filteredOptions.value[selectedIndex.value]) {
                selectOption(filteredOptions.value[selectedIndex.value]);
            }
            break;
            
        case 'Escape':
            event.preventDefault();
            dropdownVisible.value = false;
            selectedIndex.value = -1;
            inputRef.value?.blur();
            break;
            
        case 'Tab':
            dropdownVisible.value = false;
            selectedIndex.value = -1;
            break;
    }
};

const selectOption = (option: AutocompleteOption | null, skipChange = false) => {
    if (!option) {
        if (props.multiple) {
            model.value = [];
            selectedOptions.value = [];
        } else {
            model.value = "";
            selectedOptions.value = [];
        }
        inputValue.value = "";
    } else {
        if (props.multiple) {
            const currentArray = Array.isArray(model.value) ? [...model.value] : [];
            const optionValue = getOptionValue(option);
            
            // Check if already selected
            const existingIndex = currentArray.findIndex(val => {
                if (props.returnObject) {
                    return JSON.stringify(val) === JSON.stringify(optionValue);
                }
                return val === optionValue;
            });
            
            if (existingIndex >= 0) {
                // Remove if already selected
                currentArray.splice(existingIndex, 1);
                selectedOptions.value = selectedOptions.value.filter(opt => 
                    getOptionValue(opt) !== optionValue
                );
            } else {
                // Add if not selected
                currentArray.push(optionValue);
                selectedOptions.value.push(option);
            }
            
            model.value = currentArray;
            inputValue.value = ""; // Clear input for multiple selection
        } else {
            inputValue.value = getOptionDisplay(option);
            selectedOptions.value = [option];
            model.value = getOptionValue(option);
            dropdownVisible.value = false;
        }
    }
    
    selectedIndex.value = -1;
    
    if (!skipChange) {
        emit("change", model.value);
    }
    
    // Return focus to input
    nextTick(() => {
        inputRef.value?.focus();
    });
};

const clearInput = () => {
    selectOption(null);
    
    // Clear the debounced search to show all options when dropdown reopens
    debouncedSearch.value = "";
    
    // Always reopen dropdown after clearing - user explicitly wants to select again
    // Use nextTick to ensure DOM updates are complete
    nextTick(() => {
        nextTick(() => {
            dropdownVisible.value = true;
            selectedIndex.value = -1;
        });
    });
};

// Find option by model value
const findOptionByValue = (value: any): AutocompleteOption | null => {
    if (!value || !props.options) return null;
    
    return props.options.find(option => {
        const optionValue = getOptionValue(option);
        if (props.returnObject) {
            return JSON.stringify(optionValue) === JSON.stringify(value);
        }
        return optionValue == value; // Use loose equality for compatibility
    }) || null;
};

const findOptionsByValues = (values: any[]): AutocompleteOption[] => {
    if (!Array.isArray(values) || !props.options) return [];
    
    return values.map(value => findOptionByValue(value)).filter(Boolean) as AutocompleteOption[];
};

const updateDisplayValue = () => {
    if (props.multiple) {
        inputValue.value = ""; // Always empty for multiple selection
    } else if (model.value) {
        const option = findOptionByValue(model.value);
        inputValue.value = option ? getOptionDisplay(option) : "";
    } else {
        inputValue.value = "";
    }
};

// Watchers
watch(() => model.value, (newValue) => {
    if (props.multiple) {
        const values = Array.isArray(newValue) ? newValue : [];
        selectedOptions.value = findOptionsByValues(values);
    } else {
        const option = findOptionByValue(newValue);
        selectedOptions.value = option ? [option] : [];
    }
    updateDisplayValue();
}, { immediate: true });

watch(() => props.options, () => {
    // Re-evaluate the selected options when options change
    if (props.multiple) {
        const values = Array.isArray(model.value) ? model.value : [];
        selectedOptions.value = findOptionsByValues(values);
    } else if (model.value) {
        const option = findOptionByValue(model.value);
        selectedOptions.value = option ? [option] : [];
    }
    
    // Don't update display value while dropdown is open - user is actively selecting
    // This prevents interference when options load while user is clicking to open dropdown
    if (!dropdownVisible.value) {
        updateDisplayValue();
    }
});

// Watch for options changes to clear loading state
watch(() => props.options, () => {
    isLoading.value = false;
});

// Close dropdown when clicking outside
watch(dropdownVisible, (visible) => {
    if (!visible) {
        selectedIndex.value = -1;
        clearSearchTimeout();
    }
});

// Click outside detection
const handleClickOutside = (event: MouseEvent) => {
    // Don't process if click-outside is temporarily disabled
    if (!clickOutsideEnabled.value) {
        return;
    }
    
    const target = event.target as Node;
    const inputWrap = inputRef.value?.closest('.ac-input-wrap');
    
    if (
        dropdownVisible.value &&
        inputRef.value &&
        dropdownRef.value &&
        !inputRef.value.contains(target) &&
        !dropdownRef.value.contains(target) &&
        !(inputWrap && inputWrap.contains(target))
    ) {
        dropdownVisible.value = false;
    }
};

// Setup and cleanup
onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

// Cleanup on unmount
onUnmounted(() => {
    clearSearchTimeout();
    document.removeEventListener('click', handleClickOutside);
});

</script>

<style scoped>
.autocomplete {
    width: auto;
    max-width: 100%;
    position: relative;
}

.ac-input-wrap {
    position: relative;
}

.input-actions {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
}

.btn-clear {
    background: none;
    border: none;
    color: #6c757d;
    font-size: 14px;
    padding: 0;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.btn-clear:hover {
    color: #495057;
}

.loading-spinner {
    color: #6c757d;
    font-size: 14px;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.autocomplete-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.autocomplete-options {
    max-height: 250px;
    overflow-y: auto;
}

.autocomplete-option {
    padding: 0.5rem 1rem;
    cursor: pointer;
    border: none;
    background: none;
    width: 100%;
    text-align: left;
    transition: background-color 0.15s ease-in-out;
}

.autocomplete-option:hover,
.autocomplete-option.active {
    background-color: #0d6efd;
    color: white;
}

.autocomplete-option.selected {
    background-color: #e7f3ff;
    color: #0d6efd;
    border-left: 3px solid #0d6efd;
    font-weight: 500;
}

.selected-items {
    max-height: 100px;
    overflow-y: auto;
}

.selected-items .badge {
    font-size: 0.875em;
    display: inline-flex;
    align-items: center;
}

.autocomplete-option:focus {
    background-color: #e9ecef;
    outline: none;
}

/* Mobile-friendly touch targets */
@media (max-width: 768px) {
    .autocomplete-option {
        padding: 0.75rem 1rem;
        font-size: 16px; /* Prevents zoom on iOS */
    }
    
    .btn-clear,
    .loading-spinner {
        width: 24px;
        height: 24px;
        font-size: 16px;
    }
}
</style>